import { supabase } from '../lib/supabase';

const couponService = {
  // Validate and get coupon details (for checkout)
  async validateCoupon(code, orderAmount) {
    if (!code?.trim()) throw new Error('Kupon kodu boş olamaz');

    const { data: coupon, error } = await supabase
      ?.from('coupons')
      ?.select('*')
      ?.ilike('code', code?.trim())
      ?.eq('is_active', true)
      ?.single();

    if (error || !coupon) {
      throw new Error('Geçersiz kupon kodu');
    }

    // Check expiry
    if (coupon?.expires_at && new Date(coupon?.expires_at) < new Date()) {
      throw new Error('Bu kupon kodunun süresi dolmuştur');
    }

    // Check usage limit
    if (coupon?.usage_limit !== null && coupon?.usage_count >= coupon?.usage_limit) {
      throw new Error('Bu kupon kodunun kullanım limiti dolmuştur');
    }

    // Check minimum order amount
    if (coupon?.min_order_amount && orderAmount < coupon?.min_order_amount) {
      throw new Error(`Bu kupon için minimum sipariş tutarı ₺${coupon?.min_order_amount?.toFixed(2)}`);
    }

    // Calculate discount
    let discountAmount = 0;
    if (coupon?.discount_type === 'percentage') {
      discountAmount = (orderAmount * coupon?.discount_value) / 100;
      if (coupon?.max_discount_amount) {
        discountAmount = Math.min(discountAmount, coupon?.max_discount_amount);
      }
    } else {
      discountAmount = Math.min(coupon?.discount_value, orderAmount);
    }

    return {
      coupon,
      discountAmount: Math.round(discountAmount * 100) / 100,
      finalAmount: Math.max(0, orderAmount - discountAmount),
    };
  },

  // Record coupon usage after order is created
  async recordUsage({ couponId, userId, orderId, discountApplied, originalAmount, finalAmount }) {
    const { error } = await supabase?.from('coupon_usage')?.insert([{
      coupon_id: couponId,
      user_id: userId,
      order_id: orderId,
      discount_applied: discountApplied,
      original_amount: originalAmount,
      final_amount: finalAmount,
    }]);
    if (error) throw error;
  },

  // Admin: Get all coupons
  async getAllCoupons() {
    const { data, error } = await supabase
      ?.from('coupons')
      ?.select('*, user_profiles!coupons_attributed_user_id_fkey(full_name, email)')
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  // Admin: Create coupon
  async createCoupon(couponData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const payload = {
      code: couponData?.code?.trim()?.toUpperCase(),
      discount_type: couponData?.discountType,
      discount_value: Number(couponData?.discountValue),
      min_order_amount: couponData?.minOrderAmount ? Number(couponData?.minOrderAmount) : 0,
      max_discount_amount: couponData?.maxDiscountAmount ? Number(couponData?.maxDiscountAmount) : null,
      expires_at: couponData?.expiresAt || null,
      usage_limit: couponData?.usageLimit ? Number(couponData?.usageLimit) : null,
      is_active: true,
      attributed_user_id: couponData?.attributedUserId || null,
      description: couponData?.description || null,
      created_by: user?.id || null,
    };
    const { data, error } = await supabase?.from('coupons')?.insert([payload])?.select()?.single();
    if (error) throw error;
    return data;
  },

  // Admin: Update coupon
  async updateCoupon(id, updates) {
    const { data, error } = await supabase
      ?.from('coupons')
      ?.update({ ...updates, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  // Admin: Delete coupon
  async deleteCoupon(id) {
    const { error } = await supabase?.from('coupons')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  // Admin: Get coupon statistics
  async getCouponStats() {
    const { data: usageData, error } = await supabase
      ?.from('coupon_usage')
      ?.select('coupon_id, discount_applied, final_amount, used_at, coupons(code, discount_type, discount_value)');
    if (error) throw error;

    // Group by coupon_id
    const statsMap = {};
    (usageData || [])?.forEach(row => {
      const cid = row?.coupon_id;
      if (!statsMap?.[cid]) {
        statsMap[cid] = {
          coupon_id: cid,
          code: row?.coupons?.code || cid,
          discount_type: row?.coupons?.discount_type,
          discount_value: row?.coupons?.discount_value,
          usage_count: 0,
          total_discount: 0,
          total_revenue: 0,
        };
      }
      statsMap[cid].usage_count += 1;
      statsMap[cid].total_discount += Number(row?.discount_applied) || 0;
      statsMap[cid].total_revenue += Number(row?.final_amount) || 0;
    });

    return Object.values(statsMap)?.sort((a, b) => b?.usage_count - a?.usage_count);
  },

  // Admin: Get users for attribution dropdown
  async getUsers() {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('id, full_name, email')
      ?.order('full_name');
    if (error) throw error;
    return data || [];
  },

  // Calculate proportional discount allocation across cart items
  // Used for order creation to comply with accounting/refund requirements
  calculateProportionalDiscounts(cartItems, totalDiscountAmount) {
    const cartTotal = cartItems?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0);
    if (!cartTotal || cartTotal === 0) return cartItems;

    return cartItems?.map(item => {
      const lineTotal = item?.price * item?.quantity;
      const weight = lineTotal / cartTotal;
      const itemDiscount = Math.round(totalDiscountAmount * weight * 100) / 100;
      return {
        ...item,
        applied_discount: itemDiscount,
      };
    });
  },
};

export default couponService;
